import React, { memo } from 'react';
import PropTypes from 'prop-types';
import createStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
//import Carousel from '../Carousel';
import Carousel from '../ElasticCarousel';
import Box from '../../elements/Box';
import MonthlyHistogramBar from './atoms/MonthlyHistogramBar';
import MonthlyHistogramBarV2 from './atoms/MonthlyHistogramBarV2';

class MontlyHistogramCarousel extends React.Component {
  state = {
    currentPageIndex: 0
  };

  onPageChange = (oldIndex, currentPageIndex) => {
    this.setState({ currentPageIndex });
  };

  calculateMinMax = (currentSlide) => {
    const { barProportionalToVisibleMax, data, elementsToShow } = this.props;
    let max = 0;
    let min = Number.MAX_VALUE;
    if (barProportionalToVisibleMax && data[currentSlide]) {
      data.forEach((_, index) => {
        if (index >= currentSlide && index < currentSlide + elementsToShow) {
          const height = data[index].totalPrice;
          if (height && height > max) {
            max = height;
          }
          if (height && height < min) {
            min = height;
          }
        }
      });
    } else {
      data.forEach((_, key) => {
        const height = data[key].totalPrice;
        if (height && height > max) {
          max = height;
        }
        if (height && height < min) {
          min = height;
        }
      });
    }
    return { min, max };
  };

  adjustHeight = ({ value, min, max }) => {
    const { cardMaxHeight, cardMinHeight } = this.props;
    if (value === max) {
      return cardMaxHeight;
    }
    if (value === min) {
      return cardMinHeight;
    }
    const percent = ((value - min) * 100) / (max - min);
    return (percent * (cardMaxHeight - cardMinHeight)) / 100 + cardMinHeight;
  };

  render = () => {
    const {
      FareComponentInstance,
      fareItem,
      monthItem,
      prevButtonSettings,
      nextButtonSettings,
      responsive,
      showArrowsWhenDisabled,
      showMonthItem,
      monthFormat,
      sameBarsHeight,
      elementsToShow,
      elementsToScroll,
      showNavigation,
      data,
      lazyLoad,
      cardMaxHeight,
      showDots,
      cardCheckAvailabilityHeight,
      histogramBarMaxHeight,
      colSettings,
      renderMonthlyBar,
      dotSettings,
      tracking
    } = this.props;

    const { direction, theme } = this.context;
    const [className] = createStyles(this.props, { direction, theme });
    const { currentPageIndex } = this.state;
    const fareItemSettings = { fareItem, monthItem, showMonthItem };

    const { min, max } = this.calculateMinMax(currentPageIndex);

    // Check if all fares have the same price to hide the lowest tag
    const validPrices = data
      .filter((item) => item.dataType === 'DATA_API' && item.totalPrice != null)
      .map((item) => item.totalPrice);
    const allPricesEqual =
      validPrices.length > 1 && validPrices.every((price) => price === validPrices[0]);

    const effectiveBarMaxHeight =
      typeof histogramBarMaxHeight === 'number'
        ? Math.min(histogramBarMaxHeight, cardMaxHeight)
        : cardMaxHeight;

    const emptyMonths = data
      .map((item) => {
        const showHistogramBar =
          item.dataType === 'DATA_API' && typeof item.totalPrice === 'number';
        const fallbackBarHeight =
          typeof cardCheckAvailabilityHeight === 'number'
            ? Math.min(cardCheckAvailabilityHeight, effectiveBarMaxHeight)
            : 0;

        if (item.dataType === 'DATA_API') {
          const copyData = { ...item };
          const { isLowest } = copyData;
          let fareItemHeight = fallbackBarHeight;

          if (showHistogramBar) {
            if (sameBarsHeight) {
              fareItemHeight = effectiveBarMaxHeight;
            } else {
              const adjustedHeight = this.adjustHeight({ value: copyData.totalPrice, min, max });
              fareItemHeight = Math.min(adjustedHeight, effectiveBarMaxHeight);
            }
          }

          delete copyData.isLowest;
          return {
            monthFormat,
            fare: item,
            index: item.index,
            isLowest,
            allPricesEqual,
            FareComponentInstance,
            fareItemMaxHeight: effectiveBarMaxHeight,
            fareItemHeight,
            showHistogramBar
          };
        }
        return {
          monthFormat,
          fare: item,
          allPricesEqual,
          FareComponentInstance,
          fareItemMaxHeight: effectiveBarMaxHeight,
          fareItemHeight: fallbackBarHeight,
          showHistogramBar
        };
      })
      .filter((item) => item !== null);
    return (
      <Box className="fr-montly-histogram-carousel fr-w-100" dataTestId="montlyHistogramCarousel">
        {emptyMonths.length > 0 && (
          <Carousel
            className={className}
            itemsData={emptyMonths}
            ItemComponentInstance={
              renderMonthlyBar === 'MonthlyHistogramBar'
                ? MonthlyHistogramBar
                : MonthlyHistogramBarV2
            }
            itemSettings={fareItemSettings}
            showDots={showDots}
            showNavigation={showNavigation}
            elementsToShow={elementsToShow}
            elementsToScroll={elementsToScroll}
            prevButtonSettings={prevButtonSettings}
            nextButtonSettings={nextButtonSettings}
            beforeChange={this.onPageChange}
            responsive={responsive}
            colSettings={colSettings}
            lazyLoad={lazyLoad}
            fullHeight
            showArrowsWhenDisabled={showArrowsWhenDisabled}
            dotSettings={dotSettings}
            tracking={tracking}
          />
        )}
      </Box>
    );
  };
}

MontlyHistogramCarousel.propTypes = {
  /**
   * Determine if the carousel bars will have the same height
   */
  sameBarsHeight: PropTypes.bool,
  /**
   * Define how to show the months
   */
  monthFormat: PropTypes.oneOf(['shortMonths', 'fullMonths']),
  /**
   * Array of fares to show in slider
   */
  data: PropTypes.arrayOf(PropTypes.any),
  /**
   * Component to show in cad
   */
  FareComponentInstance: PropTypes.func.isRequired,
  /**
   * Number of month to visibles
   */
  elementsToShow: PropTypes.number,
  /**
   * Number of month to scroll
   */
  elementsToScroll: PropTypes.number,
  /**
   * Define if carousel navigation
   */
  showNavigation: PropTypes.bool,
  /**
   * Define if show month by each bar
   */
  showMonthItem: PropTypes.bool,
  /**
   * Fare item props
   */
  fareItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Months propperties
   */
  monthItem: PropTypes.objectOf(PropTypes.any),
  /**
   * colSettings properties
   */
  colSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * barProportionalToVisible
   */
  barProportionalToVisibleMax: PropTypes.bool,
  /**
   * Break points for responsive carousel
   */
  responsive: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  /**
   * prevButtonSettings object of settings
   */
  prevButtonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * nextButtonSettings object of settings
   */
  nextButtonSettings: PropTypes.objectOf(PropTypes.any),
  showArrowsWhenDisabled: PropTypes.bool,
  /**
   * Lazy load
   */
  lazyLoad: PropTypes.oneOf(['disabled', 'ondemand', 'progressive']),
  /**
   * Define height of the max value card
   */
  cardMaxHeight: PropTypes.number,
  /**
   * dotSettings object of settings
   */
  dotSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Define height of the min value card
   */
  cardMinHeight: PropTypes.number,
  cardCheckAvailabilityHeight: PropTypes.number,
  histogramBarMaxHeight: PropTypes.number,
  showDots: PropTypes.bool,
  renderMonthlyBar: PropTypes.oneOf(['MonthlyHistogramBar', 'MonthlyHistogramBarV2'])
};
MontlyHistogramCarousel.defaultProps = {
  sameBarsHeight: false,
  monthFormat: 'shortMonths',
  data: [],
  elementsToShow: 6,
  elementsToScroll: 6,
  showNavigation: true,
  fareItem: {},
  monthItem: {},
  colSettings: {
    padding: '0'
  },
  showMonthItem: true,
  barProportionalToVisibleMax: false,
  responsive: [],
  prevButtonSettings: null,
  nextButtonSettings: null,
  showArrowsWhenDisabled: true,
  lazyLoad: 'disabled',
  cardMaxHeight: 350,
  cardMinHeight: 150,
  cardCheckAvailabilityHeight: 150,
  histogramBarMaxHeight: 250,
  showDots: false,
  renderMonthlyBar: 'MonthlyHistogramBar'
};

MontlyHistogramCarousel.componentName = 'MontlyHistogramCarousel';

MontlyHistogramCarousel.contextType = Context;

export default memo(
  MontlyHistogramCarousel,
  (prevProps, nextProps) => prevProps.data === nextProps.data
);
